import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { CheckCircle2, Info, Plug, RefreshCw } from "lucide-react";
import request from "../../utils/request";
import { setHostInfo } from "../../store/slices/hostSlices";
import {
  getConnectedPmsProviders,
  getPmsDisplayName,
  isPmsConnected,
  normalizePmsSelections,
} from "../../utils/onboarding";

const PROVIDER_LOGOS = {
  guesty: {
    src: "https://bellhopt-public.s3.us-west-1.amazonaws.com/guesty.svg",
    alt: "Guesty",
    maxHeight: 24,
  },
  hospitable: {
    src: "https://bellhopt-public.s3.us-west-1.amazonaws.com/hospitable.svg",
    alt: "Hospitable",
    // Hospitable wordmark is ~3:1 — a touch taller works well
    maxHeight: 26,
  },
  hostfully: {
    src: "/pms/hostfully.png",
    alt: "Hostfully",
    maxHeight: 28,
  },
  streamline: {
    src: "https://bellhopt-public.s3.us-west-1.amazonaws.com/streamline.svg",
    alt: "Streamline",
    // Streamline wordmark is ~4.2:1 — render a bit shorter so it doesn't dominate
    maxHeight: 22,
  },
};

function APIKeyInput({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
      />
    </div>
  );
}

function ProviderCard({
  title,
  provider,
  connected,
  needsSetup,
  loading,
  children,
  onConnect,
  onSync,
  propertyCount,
  extraActions,
  logoSrc,
  logoAlt,
  logoMaxHeight = 28,
}) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 transition hover:border-gray-300">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {logoSrc ? (
            <div className="flex h-11 min-w-[56px] items-center justify-start">
              <img
                src={logoSrc}
                alt={logoAlt || title}
                style={{ maxHeight: logoMaxHeight }}
                className="w-auto max-w-[160px] object-contain object-left"
              />
            </div>
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
              <Plug size={19} />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">{title}</h3>
            <div className="mt-1 flex items-center gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  connected ? "bg-green-500" : needsSetup ? "bg-amber-500" : "bg-gray-400"
                }`}
              />
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                  connected
                    ? "bg-green-50 text-green-700"
                    : needsSetup
                    ? "bg-amber-50 text-amber-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {connected ? "Connected" : needsSetup ? "Needs setup" : "Disconnected"}
              </span>
              {connected && (
                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-700">
                  {propertyCount} {propertyCount === 1 ? "property" : "properties"}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {connected ? (
            <>
              {extraActions}
              {onSync && (
                <button
                  type="button"
                  onClick={onSync}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:opacity-50"
                >
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                  Sync now
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={onConnect}
              disabled={loading}
              className="rounded-xl bg-gray-900 px-5 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:opacity-50"
            >
              Connect
            </button>
          )}
        </div>
      </div>

      <div className="mt-5">{children}</div>
    </section>
  );
}

function getTrackHostSummary(summary) {
  if (Array.isArray(summary?.results)) {
    return summary.results[0] || null;
  }
  return summary || null;
}

function getHostfullyHostSummary(summary) {
  if (Array.isArray(summary?.results)) {
    return summary.results[0] || null;
  }
  return summary || null;
}

function humanizeTrackReason(reason) {
  return String(reason || "")
    .replace(/_/g, " ")
    .trim();
}

function ConnectedDetails({ rows, copy }) {
  return (
    <div className="rounded-xl border border-green-100 bg-green-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <CheckCircle2 size={16} className="text-green-600" />
        <p className="text-sm font-bold text-green-800">Connection active</p>
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 rounded-lg bg-white/70 px-3 py-2">
            <span className="text-xs font-semibold text-green-700">{row.label}</span>
            <span className="truncate text-xs font-bold text-green-900">{row.value}</span>
          </div>
        ))}
      </div>
      {copy && <p className="mt-3 text-xs text-green-700">{copy}</p>}
    </div>
  );
}

function PmsConnectionPanel({
  hostInfo,
  providerOverride,
  properties = [],
  onPropertiesRefresh,
  showAllWhenUnconfigured = false,
  source = "integrations",
  onConnected,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showGuestyForm, setShowGuestyForm] = useState(false);
  const [showStreamlineForm, setShowStreamlineForm] = useState(false);
  const [showTrackForm, setShowTrackForm] = useState(false);
  const [guestyToken, setGuestyToken] = useState("");
  const [streamlineApiKey, setStreamlineApiKey] = useState("");
  const [streamlineApiSecret, setStreamlineApiSecret] = useState("");
  const [trackKey, setTrackKey] = useState("");
  const [trackSecret, setTrackSecret] = useState("");
  const [trackBaseUrl, setTrackBaseUrl] = useState("");
  const [trackCustomFieldHandle, setTrackCustomFieldHandle] = useState("");

  const selectedProviders = providerOverride
    ? normalizePmsSelections(providerOverride)
    : normalizePmsSelections(hostInfo?.propertyManagementSoftware);
  const connectedProviders = getConnectedPmsProviders(hostInfo);
  const effectiveProviders = Array.from(new Set([...selectedProviders, ...connectedProviders]));
  const hasConfiguredPms = effectiveProviders.length > 0;
  const showGuestyCard = showAllWhenUnconfigured
    ? true
    : hasConfiguredPms
    ? effectiveProviders.includes("guesty")
    : true;
  const showStreamlineCard = showAllWhenUnconfigured
    ? true
    : hasConfiguredPms
    ? effectiveProviders.includes("streamline")
    : true;
  const showHospitableCard = showAllWhenUnconfigured
    ? true
    : effectiveProviders.includes("hospitable");
  const showHostfullyCard = showAllWhenUnconfigured
    ? true
    : effectiveProviders.includes("hostfully");
  const showTrackCard = showAllWhenUnconfigured
    ? true
    : effectiveProviders.includes("track");

  const isGuestyConnected = isPmsConnected(hostInfo, "guesty");
  const isStreamlineConnected = isPmsConnected(hostInfo, "streamline");
  const isHospitableConnected = isPmsConnected(hostInfo, "hospitable");
  const isHostfullyConnected = isPmsConnected(hostInfo, "hostfully");
  const isTrackConnected = isPmsConnected(hostInfo, "track");
  const needsStreamlineKeys = effectiveProviders.includes("streamline") && !isStreamlineConnected;
  const selectedUnsupportedProvider =
    hasConfiguredPms &&
    !effectiveProviders.some((provider) => ["guesty", "streamline", "hospitable", "hostfully", "track"].includes(provider));
  const activePropertyCount = Array.isArray(properties)
    ? properties.filter((property) => !property?.isDeleted).length
    : 0;

  useEffect(() => {
    setTrackBaseUrl(hostInfo?.trackBaseUrl || "");
    setTrackCustomFieldHandle(hostInfo?.trackBellhoptUrlCustomFieldHandle || "");
  }, [hostInfo?.trackBaseUrl, hostInfo?.trackBellhoptUrlCustomFieldHandle]);

  const mergeHostInfo = (patch) => {
    if (!hostInfo) return;
    const nextHostInfo = { ...hostInfo, ...patch };
    localStorage.setItem("hostInfo", JSON.stringify(nextHostInfo));
    dispatch(setHostInfo(nextHostInfo));
  };

  const refreshHostInfo = async () => {
    if (!hostInfo?._id) return;
    const { data } = await request.get(`/hosts/${hostInfo._id}`, { withCredentials: true });
    localStorage.setItem("hostInfo", JSON.stringify(data));
    dispatch(setHostInfo(data));
  };

  const finishConnected = (provider, patch) => {
    if (patch) mergeHostInfo(patch);
    onConnected?.(provider);
  };

  const openTrackForm = () => {
    setTrackKey("");
    setTrackSecret("");
    setTrackBaseUrl(hostInfo?.trackBaseUrl || "");
    setTrackCustomFieldHandle(hostInfo?.trackBellhoptUrlCustomFieldHandle || "");
    setShowTrackForm(true);
  };

  const closeTrackForm = () => {
    setShowTrackForm(false);
    setTrackKey("");
    setTrackSecret("");
    setTrackBaseUrl(hostInfo?.trackBaseUrl || "");
    setTrackCustomFieldHandle(hostInfo?.trackBellhoptUrlCustomFieldHandle || "");
  };

  const handleGuestyConnect = async () => {
    if (!guestyToken) {
      toast.error("Please enter a Guesty token");
      return;
    }
    setLoading(true);
    try {
      const response = await request.post(
        `/hosts/save-guesty-status/${hostInfo._id}`,
        { token: guestyToken },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(response.data.msg || "Guesty connected successfully.");
        setGuestyToken("");
        setShowGuestyForm(false);
        finishConnected("guesty", {
          guesty_client_id: response.data.guesty_client_id,
          guesty_client_secret: response.data.guesty_client_secret,
        });
      } else {
        toast.error(response.data.msg || "Failed to connect to Guesty");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStreamlineConnect = async () => {
    if (!streamlineApiKey || !streamlineApiSecret) {
      toast.error("Please enter both API Key and Secret");
      return;
    }
    setLoading(true);
    try {
      const response = await request.post(
        `/hosts/save-streamline-status/${hostInfo._id}`,
        { apiKey: streamlineApiKey, apiSecret: streamlineApiSecret },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(response.data.msg || "Streamline connected successfully.");
        setStreamlineApiKey("");
        setStreamlineApiSecret("");
        setShowStreamlineForm(false);
        finishConnected("streamline", {
          streamline_api_key: response.data.streamline_api_key,
          streamline_api_secret: response.data.streamline_api_secret,
        });
      } else {
        toast.error(response.data.msg || "Failed to connect to Streamline");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleGuestySync = async () => {
    setLoading(true);
    try {
      const response = await request.post(`/hosts/sync-guesty-properties/${hostInfo._id}`, {}, { withCredentials: true });
      toast.success(response.data.msg || "Guesty properties synced successfully.");
      await onPropertiesRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || "Failed to sync Guesty properties");
    } finally {
      setLoading(false);
    }
  };

  const handleHospitableConnect = async () => {
    setLoading(true);
    try {
      const response = await request.get(`/hosts/hospitable/oauth-url/${hostInfo._id}`, { withCredentials: true });
      if (response?.data?.success && response?.data?.oauthUrl) {
        window.location.assign(response.data.oauthUrl);
        return;
      }
      toast.error(response?.data?.message || "Failed to start Hospitable connection");
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || "Failed to start Hospitable connection");
    } finally {
      setLoading(false);
    }
  };

  const handleHospitableSync = async () => {
    setLoading(true);
    try {
      const response = await request.post(`/hosts/sync-hospitable-properties/${hostInfo._id}`, {}, { withCredentials: true });
      const summary = response?.data?.summary;
      if (summary) {
        toast.success(
          `${response.data.msg || "Hospitable sync complete."} Created: ${summary.created || 0}, Updated: ${summary.updated || 0}, Skipped: ${summary.skipped || 0}, Failed: ${summary.failed || 0}`
        );
      } else {
        toast.success(response.data.msg || "Hospitable properties synced successfully.");
      }
      await refreshHostInfo();
      await onPropertiesRefresh?.();
      onConnected?.("hospitable");
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || "Failed to sync Hospitable properties");
    } finally {
      setLoading(false);
    }
  };

  const handleHostfullyConnect = async () => {
    setLoading(true);
    try {
      const response = await request.get(`/hosts/hostfully/oauth-url/${hostInfo._id}`, { withCredentials: true });
      if (response?.data?.success && response?.data?.oauthUrl) {
        window.location.assign(response.data.oauthUrl);
        return;
      }
      toast.error(response?.data?.message || "Failed to start Hostfully connection");
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || "Failed to start Hostfully connection");
    } finally {
      setLoading(false);
    }
  };

  const handleHostfullySync = async () => {
    setLoading(true);
    try {
      const propertyResponse = await request.post(
        `/hosts/sync-hostfully-properties/${hostInfo._id}`,
        {},
        { withCredentials: true }
      );
      const reservationResponse = await request.post(
        `/hosts/sync-hostfully-reservations/${hostInfo._id}`,
        { mode: "incremental", dryRun: false },
        { withCredentials: true }
      );

      const propertySummary = propertyResponse?.data?.summary;
      const reservationSummary = getHostfullyHostSummary(reservationResponse?.data?.summary);

      toast.success(
        `Hostfully sync complete. Properties: ${propertySummary?.created || 0} created, ${propertySummary?.updated || 0} updated, ${propertySummary?.failed || 0} failed. Reservations: ${reservationSummary?.reservationsSeen || reservationSummary?.leadsSeen || 0} seen, ${reservationSummary?.upserts || 0} upserts, ${reservationSummary?.failed || 0} failed.`
      );
      await refreshHostInfo();
      await onPropertiesRefresh?.();
      onConnected?.("hostfully");
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data || error.message || "Failed to sync Hostfully data");
    } finally {
      setLoading(false);
    }
  };

  const handleHostfullyWriteback = async () => {
    setLoading(true);
    try {
      const response = await request.post(
        `/hosts/sync-hostfully-unique-links/${hostInfo._id}`,
        { dryRun: false, mode: "new" },
        { withCredentials: true }
      );
      const summary = getHostfullyHostSummary(response?.data?.summary);
      toast.success(
        `Bellhopt URL writeback complete. ${summary?.pushed || 0} pushed, ${summary?.skipped || 0} skipped, ${summary?.failed || 0} failed.`
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "Failed to push Bellhopt links into Hostfully"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTrackConnect = async () => {
    if (!trackKey || !trackSecret) {
      toast.error("Please enter both Track key and secret");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        key: trackKey,
        secret: trackSecret,
        baseUrl: trackBaseUrl.trim(),
        trackBellhoptUrlCustomFieldHandle: trackCustomFieldHandle,
      };

      const response = await request.post(`/hosts/track/connect/${hostInfo._id}`, payload, {
        withCredentials: true,
      });

      if (response.data.success) {
        toast.success(response.data.msg || "Track connected successfully.");
        closeTrackForm();
        await refreshHostInfo();
        onConnected?.("track");
      } else {
        toast.error(response.data.msg || "Failed to connect to Track");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTrackSync = async () => {
    setLoading(true);
    try {
      const propertyResponse = await request.post(
        `/hosts/sync-track-properties/${hostInfo._id}`,
        { mode: "full", dryRun: false },
        { withCredentials: true }
      );
      const reservationResponse = await request.post(
        `/hosts/sync-track-reservations/${hostInfo._id}`,
        { mode: "incremental", dryRun: false, includeAccessCodes: true },
        { withCredentials: true }
      );

      const propertySummary = getTrackHostSummary(propertyResponse?.data?.summary);
      const reservationSummary = getTrackHostSummary(reservationResponse?.data?.summary);

      toast.success(
        `Track sync complete. Properties: ${propertySummary?.created || 0} created, ${propertySummary?.updated || 0} updated, ${propertySummary?.skipped || 0} skipped, ${propertySummary?.failed || 0} failed. Reservations: ${reservationSummary?.reservationsSeen || 0} seen, ${reservationSummary?.upserts || 0} upserts, ${reservationSummary?.failed || 0} failed.`
      );
      await onPropertiesRefresh?.();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "Failed to sync Track data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTrackWriteback = async () => {
    if (!hostInfo?.trackBellhoptUrlCustomFieldHandle) {
      toast.error("Add a Track Bellhopt URL custom field handle before pushing links.");
      return;
    }

    setLoading(true);
    try {
      const response = await request.post(
        `/hosts/sync-track-unique-links/${hostInfo._id}`,
        { dryRun: false, mode: "new" },
        { withCredentials: true }
      );
      const summary = getTrackHostSummary(response?.data?.summary);

      if (summary?.skippedReason) {
        toast.info(`Bellhopt URL writeback skipped: ${humanizeTrackReason(summary.skippedReason)}`);
      } else {
        toast.success(
          `Bellhopt URL writeback complete. ${summary?.pushed || 0} pushed, ${summary?.skipped || 0} skipped, ${summary?.failed || 0} failed.`
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "Failed to push Bellhopt links into Track"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderTrackForm = (isUpdate = false) => (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
      {isUpdate && (
        <p className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
          Re-enter your Track key and secret to update the base URL or Bellhopt URL custom field handle.
        </p>
      )}
      <APIKeyInput
        label="Track API Key"
        value={trackKey}
        onChange={(event) => setTrackKey(event.target.value)}
        placeholder="Enter your Track API key"
      />
      <APIKeyInput
        label="Track API Secret"
        value={trackSecret}
        onChange={(event) => setTrackSecret(event.target.value)}
        placeholder="Enter your Track API secret"
        type="password"
      />
      <APIKeyInput
        label="Track Base URL (optional)"
        value={trackBaseUrl}
        onChange={(event) => setTrackBaseUrl(event.target.value)}
        placeholder="https://api-integration-example.tracksandbox.io"
      />
      <APIKeyInput
        label="Bellhopt URL custom field handle (optional)"
        value={trackCustomFieldHandle}
        onChange={(event) => setTrackCustomFieldHandle(event.target.value)}
        placeholder="bellhopt_url"
      />
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleTrackConnect}
          disabled={loading}
          className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? (isUpdate ? "Updating..." : "Connecting...") : isUpdate ? "Update Track" : "Connect"}
        </button>
        <button
          type="button"
          onClick={closeTrackForm}
          className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {showGuestyCard && (
        <ProviderCard
          title="Guesty Integration"
          provider="guesty"
          connected={isGuestyConnected}
          loading={loading}
          onConnect={() => setShowGuestyForm(true)}
          onSync={isGuestyConnected ? handleGuestySync : null}
          propertyCount={activePropertyCount}
          logoSrc={PROVIDER_LOGOS.guesty.src}
          logoAlt={PROVIDER_LOGOS.guesty.alt}
          logoMaxHeight={PROVIDER_LOGOS.guesty.maxHeight}
        >
          {isGuestyConnected ? (
            <ConnectedDetails
              rows={[
                { label: "Client ID", value: `${hostInfo.guesty_client_id?.slice(0, 8)}...` },
                { label: "Client Secret", value: `${hostInfo.guesty_client_secret?.slice(0, 4)}******` },
              ]}
              copy="Your account is linked to Guesty. Sync properties anytime."
            />
          ) : (
            <div className="space-y-4">
              <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-600">
                Enter your Guesty API token to connect and sync your properties automatically.
              </p>
              {showGuestyForm && (
                <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
                  <APIKeyInput
                    label="Guesty API Token"
                    value={guestyToken}
                    onChange={(event) => setGuestyToken(event.target.value)}
                    placeholder="Enter your Guesty API token"
                  />
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleGuestyConnect}
                      disabled={loading}
                      className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                      {loading ? "Connecting..." : "Connect"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowGuestyForm(false);
                        setGuestyToken("");
                      }}
                      className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </ProviderCard>
      )}

      {showStreamlineCard && (
        <ProviderCard
          title="Streamline Integration"
          provider="streamline"
          connected={isStreamlineConnected}
          needsSetup={needsStreamlineKeys}
          loading={loading}
          onConnect={() => setShowStreamlineForm(true)}
          onSync={null}
          propertyCount={activePropertyCount}
          logoSrc={PROVIDER_LOGOS.streamline.src}
          logoAlt={PROVIDER_LOGOS.streamline.alt}
          logoMaxHeight={PROVIDER_LOGOS.streamline.maxHeight}
        >
          {isStreamlineConnected ? (
            <ConnectedDetails
              rows={[
                {
                  label: "API Key",
                  value: hostInfo.streamline_api_key
                    ? `${hostInfo.streamline_api_key.slice(0, 8)}...`
                    : hostInfo.streamline_token
                    ? `${hostInfo.streamline_token.slice(0, 8)}...`
                    : "Linked",
                },
                {
                  label: "API Secret",
                  value: hostInfo.streamline_api_secret || hostInfo.streamline_token_secret ? "Stored" : "Linked",
                },
              ]}
              copy="Your account is linked to Streamline. Property count reflects the latest Bellhopt data."
            />
          ) : (
            <div className="space-y-4">
              <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-600">
                {needsStreamlineKeys
                  ? "You are registered as a Streamline host but need to provide your API credentials to sync properties."
                  : "Enter your Streamline API credentials to connect and sync your properties automatically."}
              </p>
              {showStreamlineForm && (
                <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
                  <APIKeyInput
                    label="Streamline API Key"
                    value={streamlineApiKey}
                    onChange={(event) => setStreamlineApiKey(event.target.value)}
                    placeholder="Enter your Streamline API key"
                  />
                  <APIKeyInput
                    label="Streamline API Secret"
                    value={streamlineApiSecret}
                    onChange={(event) => setStreamlineApiSecret(event.target.value)}
                    placeholder="Enter your Streamline API secret"
                    type="password"
                  />
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleStreamlineConnect}
                      disabled={loading}
                      className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                      {loading ? "Connecting..." : "Connect"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowStreamlineForm(false);
                        setStreamlineApiKey("");
                        setStreamlineApiSecret("");
                      }}
                      className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </ProviderCard>
      )}

      {showHospitableCard && (
        <ProviderCard
          title="Hospitable Integration"
          provider="hospitable"
          connected={isHospitableConnected}
          loading={loading}
          onConnect={handleHospitableConnect}
          onSync={isHospitableConnected ? handleHospitableSync : null}
          propertyCount={activePropertyCount}
          logoSrc={PROVIDER_LOGOS.hospitable.src}
          logoAlt={PROVIDER_LOGOS.hospitable.alt}
          logoMaxHeight={PROVIDER_LOGOS.hospitable.maxHeight}
        >
          {isHospitableConnected ? (
            <ConnectedDetails
              rows={[
                { label: "Account ID", value: hostInfo?.hospitableAccountId || "Linked" },
                {
                  label: "Connected",
                  value: hostInfo?.hospitableConnectedAt
                    ? new Date(hostInfo.hospitableConnectedAt).toLocaleDateString()
                    : "Recently",
                },
              ]}
              copy="Your account is linked to Hospitable. Sync properties anytime."
            />
          ) : (
            <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-600">
              Use secure OAuth to connect Hospitable and sync your properties automatically.
            </p>
          )}
        </ProviderCard>
      )}

      {showHostfullyCard && (
        <ProviderCard
          title="Hostfully Integration"
          provider="hostfully"
          connected={isHostfullyConnected}
          loading={loading}
          onConnect={handleHostfullyConnect}
          onSync={isHostfullyConnected ? handleHostfullySync : null}
          propertyCount={activePropertyCount}
          logoSrc={PROVIDER_LOGOS.hostfully.src}
          logoAlt={PROVIDER_LOGOS.hostfully.alt}
          logoMaxHeight={PROVIDER_LOGOS.hostfully.maxHeight}
        >
          {isHostfullyConnected ? (
            <div className="space-y-4">
              <ConnectedDetails
                rows={[
                  { label: "Agency UID", value: hostInfo?.hostfullyAgencyUid || "Linked" },
                  {
                    label: "Connected",
                    value: hostInfo?.hostfullyConnectedAt
                      ? new Date(hostInfo.hostfullyConnectedAt).toLocaleDateString()
                      : "Recently",
                  },
                ]}
                copy="Your account is linked to Hostfully. Sync properties and reservations anytime, and push Bellhopt URLs back into Hostfully custom data."
              />

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">Bellhopt URL writeback</p>
                    <p className="mt-1 text-xs leading-5 text-gray-600">
                      Push each property&apos;s Bellhopt shopping link into Hostfully custom data (`[$bellhopt_url$]`).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleHostfullyWriteback}
                    disabled={loading}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Push Bellhopt URLs
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-600">
              Click Connect with Hostfully to authorize Bellhopt and sync properties, reservations, guest links, and entry codes.
            </p>
          )}
        </ProviderCard>
      )}

      {showTrackCard && (
        <ProviderCard
          title="Track Integration"
          provider="track"
          connected={isTrackConnected}
          loading={loading}
          onConnect={openTrackForm}
          onSync={isTrackConnected ? handleTrackSync : null}
          propertyCount={activePropertyCount}
          extraActions={
            isTrackConnected ? (
              <button
                type="button"
                onClick={() => {
                  if (showTrackForm) {
                    closeTrackForm();
                  } else {
                    openTrackForm();
                  }
                }}
                disabled={loading}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                {showTrackForm ? "Close settings" : "Update settings"}
              </button>
            ) : null
          }
        >
          {isTrackConnected ? (
            <div className="space-y-4">
              <ConnectedDetails
                rows={[
                  { label: "Base URL", value: hostInfo?.trackBaseUrl || "Default" },
                  {
                    label: "Connected",
                    value: hostInfo?.trackConnectedAt
                      ? new Date(hostInfo.trackConnectedAt).toLocaleDateString()
                      : "Recently",
                  },
                  {
                    label: "Bellhopt URL field",
                    value: hostInfo?.trackBellhoptUrlCustomFieldHandle || "Not configured",
                  },
                ]}
                copy={
                  hostInfo?.trackBellhoptUrlCustomFieldHandle
                    ? "Your account is linked to Track. Properties and reservations can sync anytime, and Bellhopt URLs can be pushed back into Track."
                    : "Your account is linked to Track. Properties and reservations can sync anytime."
                }
              />

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">Bellhopt URL writeback</p>
                    <p className="mt-1 text-xs leading-5 text-gray-600">
                      Push each property&apos;s Bellhopt shopping link into the configured Track custom field.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTrackWriteback}
                    disabled={loading || !hostInfo?.trackBellhoptUrlCustomFieldHandle}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Push Bellhopt URLs
                  </button>
                </div>
                {!hostInfo?.trackBellhoptUrlCustomFieldHandle && (
                  <p className="mt-3 text-xs text-amber-700">
                    Add the Track custom field handle in Update settings before using writeback.
                  </p>
                )}
              </div>

              {showTrackForm && renderTrackForm(true)}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-600">
                Enter your Track API credentials to sync properties and reservations automatically. Add the Bellhopt URL custom field handle if you want Bellhopt to write property links back into Track.
              </p>
              {showTrackForm && renderTrackForm(false)}
            </div>
          )}
        </ProviderCard>
      )}

      {selectedUnsupportedProvider && (
        <section className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="flex items-start gap-3">
            <Info size={18} className="mt-0.5 flex-shrink-0 text-gray-500" />
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                {getPmsDisplayName(selectedProviders[0])} setup
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                This PMS does not have a self-serve connector in Bellhopt yet. Continue with manual property setup, then use Support if you want help validating your rollout.
              </p>
              {source !== "onboarding" && (
                <Link to="/support" className="mt-3 inline-flex text-sm font-bold text-primary-600 hover:text-primary-700">
                  Contact Support
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {!showGuestyCard && !showStreamlineCard && !showHospitableCard && !showHostfullyCard && !showTrackCard && !selectedUnsupportedProvider && (
        <section className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-sm leading-6 text-gray-600">
            No integration is configured for your selected PMS. You can update this in{" "}
            <Link to={`/settings/${hostInfo?._id}`} className="font-medium text-primary-600 hover:text-primary-700">
              Profile Settings
            </Link>
            .
          </p>
        </section>
      )}
    </div>
  );
}

export default PmsConnectionPanel;
